import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Sparkles, ArrowRight, X, Activity, Cpu, ArrowDownToLine, ArrowUpFromLine, Target } from "lucide-react";
import { Breadcrumb, GradientPanel } from "../components/common";
import { agents, agentFeed, agentStats, fiveCorner } from "../data/agents";

const statusStyle = {
  Active: "text-emerald-600 bg-emerald-50",
  Idle: "text-gray-500 bg-gray-100",
  Learning: "text-violet-600 bg-violet-50",
};
const toneDot = { info: "bg-blue-400", warn: "bg-amber-400", success: "bg-emerald-400" };

const AgentIcon = ({ name, size = 20, className = "" }) => {
  const Ic = Icons[name] || Icons.Bot;
  return <Ic size={size} className={className} />;
};

const Agents = () => {
  const [selected, setSelected] = useState(null);

  return (
    <div>
      <Breadcrumb items={[{ label: "Dashboard", path: "/a/dashboard" }, { label: "AI Agents" }]} />

      <GradientPanel>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-orange-400 flex items-center justify-center text-white shrink-0">
              <Cpu size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">AI Agents</h1>
              <p className="text-sm text-gray-500 mt-1 max-w-2xl">12 LLM-native agents automating your e-invoicing pipeline end-to-end — from ingestion and extraction to VAT reasoning, routing, and tax-authority dialogue.</p>
              <span className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full"><Cpu size={12} /> Fully autonomous · zero human-in-the-loop</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {agentStats.map((s) => (
              <div key={s.label} className="px-4 py-2 rounded-xl border border-gray-100 bg-gray-50/60 text-center min-w-[92px]">
                <div className="text-lg font-bold text-gray-900">{s.value}</div>
                <div className="text-[11px] text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pipeline strip */}
        <div className="mt-6">
          <p className="text-xs font-semibold tracking-wide text-gray-400 mb-3">AUTONOMOUS PIPELINE</p>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2">
            {agents.map((a, i) => (
              <React.Fragment key={a.id}>
                <button
                  onClick={() => setSelected(a)}
                  title={a.name}
                  className={`group shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${a.accent} text-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform`}
                >
                  <AgentIcon name={a.icon} size={18} />
                </button>
                {i < agents.length - 1 && <ArrowRight size={14} className="text-gray-300 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </GradientPanel>

      {/* Peppol 5-corner framework */}
      <div className="bg-white rounded-2xl border border-gray-100 card-soft p-6 mt-4 fade-up">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-gray-900">Peppol 5-corner delivery</h3>
            <p className="text-sm text-gray-500">Every invoice is routed across all five corners automatically by the agents.</p>
          </div>
          <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">Agent-orchestrated</span>
        </div>
        <div className="flex items-stretch gap-2 overflow-x-auto pb-2 mt-5">
          {fiveCorner.map((c, i) => {
            const Ic = Icons[c.icon] || Icons.Circle;
            return (
              <React.Fragment key={c.corner}>
                <div className="flex-1 min-w-[150px] rounded-xl border border-gray-100 p-4 hover-lift">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${c.color} text-white flex items-center justify-center`}><Ic size={18} /></div>
                  <p className="text-xs font-bold text-gray-400 mt-3">{c.corner}</p>
                  <p className="font-semibold text-gray-900 text-sm">{c.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{c.desc}</p>
                </div>
                {i < fiveCorner.length - 1 && <div className="flex items-center"><ArrowRight size={16} className="text-gray-300" /></div>}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Agent grid */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {agents.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              className="text-left bg-white rounded-2xl border border-gray-100 card-soft hover-lift p-4 fade-up"
            >
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${a.accent} text-white flex items-center justify-center`}>
                  <AgentIcon name={a.icon} size={18} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-300 font-semibold">#{String(a.id).padStart(2, "0")}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusStyle[a.status]}`}>{a.status}</span>
                </div>
              </div>
              <h3 className="font-semibold text-gray-900 mt-3 leading-snug">{a.name}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{a.tagline}</p>
              <p className="text-sm text-gray-500 mt-2 line-clamp-2">{a.role}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{a.category}</span>
                <span className="text-xs text-blue-600 font-medium inline-flex items-center gap-1">View details <ArrowRight size={13} /></span>
              </div>
            </button>
          ))}
        </div>

        {/* Activity feed */}
        <div className="bg-white rounded-2xl border border-gray-100 card-soft p-5 h-fit lg:sticky lg:top-6 fade-up">
          <div className="flex items-center gap-2">
            <Activity size={17} className="text-violet-500" />
            <h3 className="font-semibold text-gray-900">Agent activity</h3>
            <span className="ml-auto flex items-center gap-1 text-[11px] text-emerald-600"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> live</span>
          </div>
          <div className="mt-4 space-y-3">
            {agentFeed.map((f, i) => {
              const ag = agents.find((a) => a.id === f.agent);
              return (
                <div key={i} className="flex gap-3">
                  <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${ag.accent} text-white flex items-center justify-center shrink-0`}>
                    <AgentIcon name={ag.icon} size={13} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-700 leading-snug">{f.text}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${toneDot[f.tone]}`} />
                      <span className="text-[11px] text-gray-400">{ag.name.split(" ")[0]} · {f.time}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {selected && <AgentDrawer agent={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2 text-sm text-gray-600">
    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
    <span>{children}</span>
  </li>
);

const AgentDrawer = ({ agent, onClose }) => (
  <div className="fixed inset-0 z-50 flex justify-end">
    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto animate-[fadeUp_.3s_ease]" style={{ animation: "fadeUp .3s ease" }}>
      <div className={`bg-gradient-to-br ${agent.accent} p-6 text-white`}>
        <div className="flex items-start justify-between">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <AgentIcon name={agent.icon} size={24} />
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center transition"><X size={18} /></button>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className="text-xs font-semibold bg-white/20 px-2 py-0.5 rounded">Agent #{String(agent.id).padStart(2, "0")}</span>
          <span className="text-xs font-semibold bg-white/20 px-2 py-0.5 rounded">{agent.category}</span>
          <span className="text-xs font-semibold bg-white/20 px-2 py-0.5 rounded">{agent.status}</span>
        </div>
        <h2 className="text-xl font-bold mt-3 leading-snug">{agent.name}</h2>
        <p className="text-sm text-white/85 mt-1">{agent.role}</p>
      </div>

      <div className="p-6 space-y-6">
        <section>
          <h4 className="text-xs font-bold tracking-wide text-gray-400 mb-2">WHAT IT DOES</h4>
          <ul className="space-y-1.5">{agent.does.map((d, i) => <Bullet key={i}>{d}</Bullet>)}</ul>
        </section>
        <section>
          <h4 className="text-xs font-bold tracking-wide text-gray-400 mb-2 flex items-center gap-1.5"><Sparkles size={13} className="text-violet-500" /> LLM USE</h4>
          <ul className="space-y-1.5">{agent.llm.map((d, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" /><span>{d}</span></li>
          ))}</ul>
        </section>
        <section className="grid grid-cols-1 gap-3">
          <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
            <p className="text-[11px] font-bold text-gray-400 flex items-center gap-1.5 mb-1"><ArrowDownToLine size={13} /> INPUT</p>
            <p className="text-sm text-gray-700">{agent.input}</p>
          </div>
          <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
            <p className="text-[11px] font-bold text-gray-400 flex items-center gap-1.5 mb-1"><ArrowUpFromLine size={13} /> OUTPUT</p>
            <p className="text-sm text-gray-700">{agent.output}</p>
          </div>
        </section>
        <section className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-4">
          <p className="text-[11px] font-bold text-emerald-600 flex items-center gap-1.5 mb-1"><Target size={13} /> OUTCOME</p>
          <p className="text-sm text-gray-700">{agent.outcome}</p>
        </section>
        <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
          <span className="text-gray-400">Events processed</span>
          <span className="font-semibold text-gray-900">{agent.events.toLocaleString()}</span>
        </div>
      </div>
    </div>
  </div>
);

export default Agents;
